import type {
  SharedSentryJsOptions,
  SharedSentryJsOptionsInput,
} from "./contracts";
import { normalizeObservabilityEnvironment } from "./environment";
import { resolveSharedRelease } from "./release";

export function limitSentryUserFields<T extends { user?: unknown }>(event: T): T {
  const user = event.user as
    | ({ id?: unknown; ip_address?: unknown } & Record<string, unknown>)
    | null
    | undefined;
  if (user) {
    const kept: { id?: unknown; ip_address?: unknown } = {};
    if (user.id != null) kept.id = user.id;
    if (user.ip_address != null) kept.ip_address = user.ip_address;
    event.user = (Object.keys(kept).length > 0 ? kept : undefined) as T["user"];
  }
  return event;
}

function normalizeTags(
  inputTags: SharedSentryJsOptionsInput["tags"],
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(inputTags ?? {}).filter(
      (entry): entry is [string, string] =>
        typeof entry[0] === "string" && typeof entry[1] === "string",
    ),
  );
}

export function buildSharedSentryJsOptions(
  input: SharedSentryJsOptionsInput,
): SharedSentryJsOptions {
  const environment = normalizeObservabilityEnvironment(input.environment);
  const release = resolveSharedRelease({
    surface: input.surface,
    release: input.release,
    releaseIdentity: input.releaseIdentity,
  });

  return {
    dsn: input.dsn ?? undefined,
    enabled: input.enabled ?? Boolean(input.dsn),
    environment,
    release,
    sendDefaultPii: input.sendDefaultPii ?? false,
    beforeSend: limitSentryUserFields,
    tracesSampleRate: input.tracesSampleRate ?? 0,
    tags: {
      "runtime.surface": input.surface,
      ...normalizeTags(input.tags),
    },
  };
}
